<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { $api } from '@/services';
import { _ROUTER_NAME } from '@/enums';
import { studentStateShort } from '@/helper/studentState';
import Loading from '@/components/common/Loading.vue';
import ScreeningExitModal from './features/ScreeningExitModal.vue';
import ScreeningSendModal from './features/ScreeningSendModal.vue';
import ScreeningResultForm from './features/ScreeningResultForm.vue';
import ScreeningCompleted from './features/ScreeningCompleted.vue';
import ScreeningInsights from './features/ScreeningInsights.vue';
import ScreeningDeleteModal from './features/ScreeningDeleteModal.vue';
import clipboardIcon from '@/assets/images/screening/detail/clipboard-check.svg';
import listIcon from '@/assets/images/screening/detail/clipboard-list.svg';
import chevronDownIcon from '@/assets/images/screening/detail/chevron-down.svg';
import chevronUpIcon from '@/assets/images/screening/detail/chevron-up.svg';
import bookCheckIcon from '@/assets/images/screening/book-check-fill.svg';
import bulbIcon from '@/assets/images/screening/bulb.svg';
import closeSourceIcon from '@/assets/images/screening/close-source.svg';
import closeGuideIcon from '@/assets/images/screening/close-guide.svg';

const route = useRoute();
const router = useRouter();
const store = useStore();

const studentId = computed(() => route.params.studentId);
const screeningId = computed(() => route.params.screeningId);

const loading = ref(true);
const saving = ref(false);
const record = ref(null);
const detail = ref(null);
const studentDetail = ref(null);
const scaleDetail = ref(null);
const question = ref(null);
const page = ref(1);
const total = ref(1);
const selectedChoiceId = ref(null);
const view = ref('question');
const result = ref(null);
const exitOpen = ref(false);
const sendOpen = ref(false);
const sendingParent = ref(false);
const deleteOpen = ref(false);
const deleting = ref(false);
const editing = ref(false);
const formOpen = ref(false);
const formInfo = ref(null);
const loadingSimple = ref(false);
const submittingResult = ref(false);
const resultSaved = ref(false);
const answerMap = ref({});
const sourceText = ref('');
const guideText = ref('');
const sourceOpen = ref(true);
const guideOpen = ref(true);
const scoreQuestions = ref([]);
const scoresOpen = ref(true);
const openedScores = ref({});

const sourceKeys = [
	'source',
	'reference',
	'references',
	'bibliography',
	'source_content',
];
const guideKeys = [
	'guide',
	'instruction',
	'guideline',
	'scoring_guide',
	'evaluation_guide',
	'how_to',
];

const unwrapList = (body) => {
	if (Array.isArray(body)) return body;
	if (Array.isArray(body?.data)) return body.data;
	if (Array.isArray(body?.screening_scale_questions)) {
		return body.screening_scale_questions;
	}
	return [];
};

const pickText = (source, keys) => {
	if (!source || typeof source !== 'object') return '';
	for (const key of keys) {
		const value = source[key];
		if (typeof value === 'string' && value.trim()) return value.trim();
	}
	return '';
};

const rememberCopy = (...sources) => {
	sources.forEach((source) => {
		if (!sourceText.value) sourceText.value = pickText(source, sourceKeys);
		if (!guideText.value) guideText.value = pickText(source, guideKeys);
		const nested = source?.screening_scale;
		if (nested) {
			if (!sourceText.value)
				sourceText.value = pickText(nested, sourceKeys);
			if (!guideText.value) guideText.value = pickText(nested, guideKeys);
		}
	});
};

const errorText = (err) => {
	const errors = err?.response?.data?.errors;
	if (Array.isArray(errors)) return errors.join('<br/>');
	if (errors && typeof errors === 'object') {
		return Object.values(errors).flat().join('<br/>');
	}
	if (typeof err?.response?.data?.error === 'string') {
		return err.response.data.error;
	}
	if (typeof err?.response?.data?.message === 'string') {
		return err.response.data.message;
	}
	return 'Đã có lỗi xảy ra. Vui lòng thử lại';
};

const alertError = async (err) => {
	await store.dispatch('setAlertMessage', {
		message: errorText(err),
		type: 'danger',
	});
};

const student = computed(
	() => studentDetail.value || record.value?.student || {}
);

const scaleName = computed(() => {
	if (scaleDetail.value?.full_english_name) {
		return scaleDetail.value.full_english_name;
	}
	const description = scaleDetail.value?.description || '';
	const match = String(description).match(/\(([^)]+)\)/);
	if (match) return match[1];
	return record.value?.screening_scale?.name || '';
});

const scaleSource = computed(() => {
	return (
		scaleDetail.value?.reference_source ||
		result.value?.reference_source ||
		''
	);
});

const crumb = computed(() => {
	const name = record.value?.screening_scale?.name || '';
	if (!name) return 'Thang sàng lọc';
	const short = String(name).split(' - ')[0];
	return /^thang\s+/i.test(short) ? short : `Thang ${short}`;
});

const ageText = (value) => {
	if (value === null || value === undefined || value === '') return '';
	if (typeof value === 'number') return `${value} tháng tuổi`;
	return String(value);
};

const ageLine = computed(() => {
	const age = ageText(
		record.value?.display_age ||
			studentDetail.value?.display_age ||
			studentDetail.value?.age
	);
	const state = studentDetail.value?.state || record.value?.student?.state;
	const stateLabel = state ? studentStateShort(state) : '';
	return [age, stateLabel].filter(Boolean).join(' | ');
});

const choices = computed(() => {
	const list = question.value?.choices || [];
	return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
});

const isLast = computed(() => page.value >= total.value);

const answeredCount = computed(() => {
	const ids = new Set(
		Object.entries(answerMap.value)
			.filter(([, value]) => value?.screening_scale_choice_id)
			.map(([id]) => String(id))
	);
	const currentId =
		question.value?.id || question.value?.screening_scale_question_id;
	if (currentId && selectedChoiceId.value) ids.add(String(currentId));
	return ids.size;
});

const progress = computed(() => {
	if (!total.value) return 0;
	return Math.min(100, Math.round((answeredCount.value / total.value) * 100));
});

const formatScore = (score) => {
	const number = Number(score);
	if (Number.isNaN(number)) return score;
	return Number.isInteger(number) ? String(number) : String(number);
};

const choiceIdFrom = (item) => {
	const answer = item?.answer;
	if (answer === null || answer === undefined || answer === '') return null;
	const list = item?.choices || [];
	if (typeof answer === 'number' || typeof answer === 'string') {
		const found = list.find(
			(choice) => String(choice.id) === String(answer)
		);
		return found ? found.id : answer;
	}
	const id =
		answer.screening_scale_choice_id ||
		answer.choice_id ||
		answer.screening_scale_choice?.id ||
		answer.choice?.id;
	if (id) return id;
	if (answer.score !== null && answer.score !== undefined) {
		const found = list.find(
			(choice) => Number(choice.score) === Number(answer.score)
		);
		return found?.id || null;
	}
	return null;
};

const loadRecord = async () => {
	try {
		const res = await $api.screening.list({
			student_id: studentId.value,
		});
		const list = unwrapList(res?.data);
		record.value =
			list.find(
				(item) => String(item.id) === String(screeningId.value)
			) || null;
		rememberCopy(record.value, record.value?.screening_scale);
		const scaleId = record.value?.screening_scale?.id;
		if (scaleId) {
			const scalesRes = await $api.screening.scales();
			scaleDetail.value =
				unwrapList(scalesRes?.data).find(
					(item) => item.id === scaleId
				) || null;
			rememberCopy(scaleDetail.value);
			if (!guideText.value && scaleDetail.value?.description) {
				guideText.value = scaleDetail.value.description;
			}
		}
	} catch (err) {
		record.value = null;
	}
};

const loadStudent = async () => {
	try {
		const res = await $api.student.getStudent(String(studentId.value));
		const body = res?.data || {};
		studentDetail.value =
			body.data && !Array.isArray(body.data) ? body.data : body;
	} catch (err) {
		studentDetail.value = null;
	}
};

const rememberAnswer = (questionId, choiceId) => {
	if (!questionId || !choiceId) return;
	answerMap.value = {
		...answerMap.value,
		[String(questionId)]: {
			screening_scale_choice_id: choiceId,
			screening_scale_question_id: questionId,
		},
	};
};

const loadQuestion = async () => {
	const res = await $api.screening.questions(
		studentId.value,
		screeningId.value,
		{ page: page.value, page_size: 1 }
	);
	const body = res?.data || {};
	const list = unwrapList(body);
	const item = list[0] || null;
	question.value = item;
	selectedChoiceId.value = item ? choiceIdFrom(item) : null;
	if (item && selectedChoiceId.value) {
		rememberAnswer(
			item.id || item.screening_scale_question_id,
			selectedChoiceId.value
		);
	}
	const count =
		item?.question_count ||
		body.question_count ||
		body.meta?.total_count ||
		body.meta?.total ||
		body.total_count;
	if (count) total.value = Number(count);
	rememberCopy(body, item, body.screening_scale);
};

const unwrapDetail = (body) => {
	if (body?.data && !Array.isArray(body.data)) return body.data;
	return body || null;
};

const loadDetail = async () => {
	try {
		const res = await $api.screening.detail(
			studentId.value,
			screeningId.value
		);
		detail.value = unwrapDetail(res?.data);
	} catch (err) {
		detail.value = null;
	}
};

const isFinished = (item) => item?.state && item.state !== 'draft';

const seedAnswers = (item) => {
	const answers = Array.isArray(item?.answers) ? item.answers : [];
	const seeded = {};
	answers.forEach((answer) => {
		const questionId = answer?.screening_scale_question_id;
		const choiceId = answer?.screening_scale_choice_id;
		if (!questionId || !choiceId) return;
		seeded[String(questionId)] = {
			screening_scale_choice_id: choiceId,
			screening_scale_question_id: questionId,
		};
	});
	if (Object.keys(seeded).length) {
		answerMap.value = { ...answerMap.value, ...seeded };
	}
};

const resumePage = (item) => {
	const questions = [...(item?.questions || [])].sort(
		(a, b) => (a.position || 0) - (b.position || 0)
	);
	const answeredIds = new Set(
		(Array.isArray(item?.answers) ? item.answers : [])
			.filter((answer) => answer?.screening_scale_choice_id)
			.map((answer) => String(answer.screening_scale_question_id))
	);
	if (questions.length) {
		const pending = questions.find((entry) => {
			const id = entry.id || entry.screening_scale_question_id;
			return !answeredIds.has(String(id));
		});
		if (!pending) return null;
		const index = questions.indexOf(pending);
		return pending.position || index + 1;
	}
	const totalCount = Number(item?.question_count) || 0;
	if (totalCount && answeredIds.size >= totalCount) return null;
	return answeredIds.size + 1;
};

const questionIdOf = (item, index = 0) =>
	item?.id || item?.screening_scale_question_id || index;

const choicesOfQuestion = (item) => {
	const list = item?.choices || item?.screening_scale_choices || [];
	return [...list].sort((a, b) => (a.position || 0) - (b.position || 0));
};

const selectedChoiceIdOf = (item) => {
	const mapped =
		answerMap.value[String(questionIdOf(item))]?.screening_scale_choice_id;
	if (mapped) return mapped;
	return choiceIdFrom(item);
};

const scoreLabel = (score) => {
	if (score === null || score === undefined || score === '') return '';
	return `${formatScore(score)} điểm`;
};

const questionScoreLabel = (item) => {
	const selectedId = selectedChoiceIdOf(item);
	const found = (item?.choices || choicesOfQuestion(item)).find(
		(choice) => String(choice.id) === String(selectedId)
	);
	if (found) return scoreLabel(found.score);
	const score = item?.answer?.score ?? item?.score;
	return scoreLabel(score) || '-';
};

const choiceSelected = (item, choice) =>
	String(selectedChoiceIdOf(item) || '') === String(choice?.id || '');

const applyScoreQuestions = (list, answers) => {
	const sorted = [...(Array.isArray(list) ? list : [])].sort(
		(a, b) => (a.position || 0) - (b.position || 0)
	);
	const byQuestion = {};
	(Array.isArray(answers) ? answers : []).forEach((answer) => {
		const id = answer?.screening_scale_question_id;
		if (!id) return;
		byQuestion[String(id)] = answer;
		if (answer.screening_scale_choice_id) {
			rememberAnswer(id, answer.screening_scale_choice_id);
		}
	});
	scoreQuestions.value = sorted.map((item) => {
		const answer = item?.answer || byQuestion[String(questionIdOf(item))];
		const next = answer ? { ...item, answer } : { ...item };
		next.choices = choicesOfQuestion(next);
		return next;
	});
	const first = scoreQuestions.value[0];
	openedScores.value = first ? { [questionIdOf(first)]: true } : {};
};

const questionTotal = (body, list, fallback) => {
	const raw =
		list?.[0]?.question_count ||
		body?.question_count ||
		body?.meta?.total_count ||
		body?.meta?.total ||
		body?.total_count ||
		fallback;
	const count = Number(raw);
	return Number.isNaN(count) ? 0 : count;
};

const loadScoreQuestions = async (answers) => {
	const expected = Math.max(Number(total.value) || 0, 1);
	const first = await $api.screening.questions(
		studentId.value,
		screeningId.value,
		{ page: 1, page_size: expected }
	);
	const body = first?.data || {};
	let list = unwrapList(body);
	const count = Math.min(questionTotal(body, list, expected), 100);
	if (count > list.length) {
		const pages = await Promise.all(
			Array.from({ length: count }, (_, index) =>
				$api.screening.questions(studentId.value, screeningId.value, {
					page: index + 1,
					page_size: 1,
				})
			)
		);
		list = pages.map((res) => unwrapList(res?.data)[0]).filter(Boolean);
	}
	applyScoreQuestions(list, answers);
};

const showResult = async () => {
	const res = await $api.screening.result(studentId.value, screeningId.value);
	const body = res?.data || {};
	result.value = body.data && !Array.isArray(body.data) ? body.data : body;
	rememberCopy(result.value);
	const embedded =
		result.value?.questions || result.value?.screening_scale_questions;
	const answers = result.value?.answers;
	const embeddedReady =
		Array.isArray(embedded) &&
		embedded.some((item) => choicesOfQuestion(item).length);
	if (embeddedReady) {
		applyScoreQuestions(embedded, answers);
	} else {
		try {
			await loadScoreQuestions(answers);
		} catch (err) {
			scoreQuestions.value = [];
		}
		if (!scoreQuestions.value.length && Array.isArray(embedded)) {
			applyScoreQuestions(embedded, answers);
		}
	}
	view.value = 'result';
};

const toggleScores = () => {
	scoresOpen.value = !scoresOpen.value;
};

const toggleScoreQuestion = (id) => {
	openedScores.value = {
		...openedScores.value,
		[id]: !openedScores.value[id],
	};
};

const resumeDraft = async (item) => {
	seedAnswers(item);
	if (item?.questions?.length) total.value = item.questions.length;
	const nextPage = resumePage(item);
	if (nextPage === null) {
		await showResult();
		return;
	}
	page.value = nextPage;
	await loadQuestion();
};

const load = async () => {
	loading.value = true;
	try {
		await Promise.all([loadStudent(), loadDetail()]);
		if (route.query.view === 'detail' || isFinished(detail.value)) {
			if (!detail.value) await loadRecord();
			detail.value = detail.value || record.value;
			record.value = detail.value;
			if (detail.value) {
				view.value = 'completed';
				return;
			}
		}
		await loadRecord();
		if (isFinished(record.value)) {
			detail.value = detail.value || record.value;
			record.value = detail.value;
			view.value = 'completed';
			return;
		}
		await resumeDraft(detail.value || record.value);
	} catch (err) {
		await alertError(err);
	} finally {
		loading.value = false;
	}
};

const saveAnswer = async () => {
	await $api.screening.saveAnswer(studentId.value, screeningId.value, {
		screening_scale_question_id: question.value.id,
		screening_scale_choice_id: selectedChoiceId.value,
	});
	rememberAnswer(question.value.id, selectedChoiceId.value);
};

const scrollToTop = () => {
	window.scrollTo({ top: 0, behavior: 'smooth' });
};

const prev = async () => {
	if (page.value <= 1 || saving.value) return;
	page.value -= 1;
	loading.value = true;
	try {
		await loadQuestion();
		scrollToTop();
	} catch (err) {
		page.value += 1;
		await alertError(err);
	} finally {
		loading.value = false;
	}
};

const next = async () => {
	if (!selectedChoiceId.value || saving.value) return;
	saving.value = true;
	try {
		await saveAnswer();
		if (isLast.value) {
			await showResult();
			scrollToTop();
		} else {
			page.value += 1;
			await loadQuestion();
			scrollToTop();
		}
	} catch (err) {
		await alertError(err);
	} finally {
		saving.value = false;
	}
};

const backToQuestion = async () => {
	view.value = 'question';
	page.value = total.value || 1;
	loading.value = true;
	try {
		await loadQuestion();
		scrollToTop();
	} catch (err) {
		await alertError(err);
	} finally {
		loading.value = false;
	}
};

const fileOf = (file) => {
	if (!file) return null;
	if (typeof file === 'string') return { name: file, url: file };
	return {
		name: file.name || file.filename || file.file_name || 'Tài liệu',
		url: file.url || file.file_url || file.link || file.path || '',
	};
};

const bandsFrom = (source) => {
	if (!source) return [];
	if (Array.isArray(source.conclusions) && source.conclusions.length) {
		return [...source.conclusions]
			.sort((a, b) => (a.position || 0) - (b.position || 0))
			.map((item) => ({
				range: `${item.min_score} - ${item.max_score} điểm`,
				label: item.label || '',
			}));
	}
	const raw =
		source.score_bands ||
		source.score_ranges ||
		source.scoring ||
		source.score_guide;
	if (typeof raw === 'string') return raw;
	if (!Array.isArray(raw)) return [];
	return raw
		.map((item) => {
			if (typeof item === 'string') return { range: item, label: '' };
			return {
				range: item.range || item.score || item.title || '',
				label:
					item.label ||
					item.conclusion ||
					item.content ||
					item.name ||
					'',
			};
		})
		.filter((item) => item.range || item.label);
};

const closeResult = () => {
	formOpen.value = false;
	resultSaved.value = false;
};

const submitResult = async (payload) => {
	if (submittingResult.value || resultSaved.value) return;
	submittingResult.value = true;
	try {
		await $api.screening.submit(studentId.value, screeningId.value, {
			state: 'completed',
			title: payload.title,
			note: payload.comment || '',
		});
		leave();
	} catch (err) {
		await alertError(err);
	} finally {
		submittingResult.value = false;
	}
};

const openSimple = async () => {
	if (loadingSimple.value) return;
	loadingSimple.value = true;
	try {
		const res = await $api.screening.simple(
			studentId.value,
			screeningId.value
		);
		const body = res?.data || {};
		const info = body.data && !Array.isArray(body.data) ? body.data : body;
		const dateValue =
			info.date || result.value?.date || record.value?.date || '';
		const scale =
			info.screening_scale || record.value?.screening_scale || {};
		let bands = bandsFrom(info);
		if (
			(!bands || (Array.isArray(bands) && !bands.length)) &&
			bandsFrom(scale)
		) {
			bands = bandsFrom(scale);
		}
		if (
			(!bands || (Array.isArray(bands) && !bands.length)) &&
			/c\.?a\.?r\.?s/i.test(`${scale.name || ''} ${crumb.value}`)
		) {
			bands = [
				{ range: '15 - 30 điểm', label: 'Trẻ bình thường' },
				{ range: '30 - 36 điểm', label: 'Tự kỷ nhẹ đến trung bình' },
				{ range: '36 - 60 điểm', label: 'Tự kỷ nặng' },
			];
		}
		formInfo.value = {
			title: info.title || record.value?.title || '',
			date: String(dateValue).slice(0, 10),
			conclusion:
				conclusionLabelOf(info.conclusion) ||
				conclusionLabelOf(result.value?.conclusion),
			totalScore:
				info.total_score ??
				result.value?.total_score ??
				record.value?.total_score,
			comment:
				info.comment || info.note || info.remark || info.nhan_xet || '',
			bands,
			files: (info.files || []).map(fileOf).filter(Boolean),
		};
		formOpen.value = true;
	} catch (err) {
		await alertError(err);
	} finally {
		loadingSimple.value = false;
	}
};

const resultPercent = computed(() => {
	const data = result.value || {};
	const raw = data.percentage ?? data.percent ?? data.progress_percent;
	if (raw !== null && raw !== undefined && raw !== '') {
		const number = Number(raw);
		if (!Number.isNaN(number)) return Math.max(0, Math.min(100, number));
	}
	const score = Number(data.total_score);
	const max = Number(
		data.max_total_score ??
			data.max_score ??
			data.total_max_score ??
			data.max ??
			scaleDetail.value?.max_total_score ??
			record.value?.screening_scale?.max_total_score
	);
	if (!Number.isNaN(score) && max) {
		return Math.max(0, Math.min(100, Math.round((score / max) * 100)));
	}
	return null;
});

const conclusionLabelOf = (value) => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	return value.label || value.name || value.title || '';
};

const conclusionLabel = computed(
	() => conclusionLabelOf(result.value?.conclusion) || '—'
);

const resultPercentLabel = computed(() => {
	if (resultPercent.value === null) return '—';
	const rounded = Math.round(resultPercent.value * 10) / 10;
	return `${rounded}%`;
});

const ringRadius = 46;
const ringLength = 2 * Math.PI * ringRadius;
const ringOffset = computed(() => {
	const percent = resultPercent.value || 0;
	return ringLength - (ringLength * percent) / 100;
});

const ringColor = computed(() => {
	const score = Number(
		result.value?.total_score ?? record.value?.total_score
	);
	if (Number.isNaN(score) || score < 30) return '#20C65B';
	if (score < 36) return '#F59E0B';
	return '#F87171';
});

const formatDateTime = (value) => {
	if (!value) return '-';
	const text = String(value).replace('T', ' ');
	const [date, time] = text.split(' ');
	const [year, month, day] = date.split('-');
	if (!year || !month || !day) return String(value);
	const clock = time ? time.slice(0, 5) : '';
	const shown = `${Number(day)}/${Number(month)}/${year}`;
	return clock ? `${shown} ${clock}` : shown;
};

const leave = () => {
	router.push({ name: _ROUTER_NAME.SCREENING });
};

const confirmSendParent = async () => {
	if (sendingParent.value) return;
	sendingParent.value = true;
	try {
		await $api.screening.send(studentId.value, screeningId.value);
		if (detail.value) {
			detail.value = {
				...detail.value,
				state: 'sent',
				sent_at: detail.value.sent_at || new Date().toISOString(),
			};
		}
		sendOpen.value = false;
	} catch (err) {
		await alertError(err);
	} finally {
		sendingParent.value = false;
	}
};

const startEdit = async () => {
	editing.value = true;
	view.value = 'question';
	page.value = 1;
	const seeded = {};
	(detail.value?.questions || []).forEach((item) => {
		const id = item.id || item.screening_scale_question_id;
		const choiceId = choiceIdFrom(item);
		if (!id || !choiceId) return;
		seeded[String(id)] = {
			screening_scale_choice_id: choiceId,
			screening_scale_question_id: id,
		};
	});
	answerMap.value = { ...answerMap.value, ...seeded };
	if (detail.value?.questions?.length) {
		total.value = detail.value.questions.length;
	}
	if (!record.value) record.value = detail.value;
	loading.value = true;
	try {
		await loadQuestion();
		scrollToTop();
	} catch (err) {
		view.value = 'completed';
		await alertError(err);
	} finally {
		loading.value = false;
	}
};

const confirmDelete = async () => {
	if (deleting.value) return;
	deleting.value = true;
	try {
		await $api.screening.remove(studentId.value, screeningId.value);
		leave();
	} catch (err) {
		await alertError(err);
	} finally {
		deleting.value = false;
	}
};

const onExit = () => {
	if (view.value === 'completed') {
		leave();
		return;
	}
	exitOpen.value = true;
};

onMounted(load);
</script>

<template>
	<div class="take" :class="{ 'take--done': view === 'completed' }">
		<header class="take__header">
			<div class="take__header-inner">
				<div class="w-full">
					<div class="flex justify-between">
						<div class="take__crumbs">
							<span>{{ crumb }}</span>
							<svg
								width="20"
								height="20"
								viewBox="0 0 20 20"
								fill="none"
							>
								<path
									d="M8 5l5 5-5 5"
									stroke="#94A3B8"
									stroke-width="1.6"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<span>{{
								view === 'completed'
									? 'Chi tiết'
									: editing
									? 'Chỉnh sửa'
									: 'Tạo mới'
							}}</span>
						</div>
						<button
							type="button"
							class="take__exit"
							@click="onExit"
						>
							<svg
								class="text-neutral-400"
								fill="none"
								height="16"
								viewBox="0 0 16 16"
								width="16"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M1.5 7.99996L0.719131 7.37526L0.219375 7.99996L0.719131 8.62465L1.5 7.99996ZM7.5 8.99996C8.05228 8.99996 8.5 8.55224 8.5 7.99996C8.5 7.44767 8.05228 6.99996 7.5 6.99996V8.99996ZM3.3858 4.04193L0.719131 7.37526L2.28087 8.62465L4.94754 5.29132L3.3858 4.04193ZM0.719131 8.62465L3.3858 11.958L4.94754 10.7086L2.28087 7.37526L0.719131 8.62465ZM1.5 8.99996H7.5V6.99996H1.5V8.99996Z"
									fill="currentColor"
								/>
								<path
									d="M7.78738 13.3439L8.31032 12.4915L7.78738 13.3439ZM6.97976 12.3905L6.05301 12.7662L6.97976 12.3905ZM14.499 12.5015L15.3875 12.9603L14.499 12.5015ZM12.7525 13.981L12.4461 13.0291L12.7525 13.981ZM14.499 3.49854L15.3875 3.0398L14.499 3.49854ZM13.8333 6.81921V9.18087H15.8333V6.81921H13.8333ZM7.83325 5.42133V5.29122H5.83325V5.42133H7.83325ZM9.58524 12.7782C8.62733 12.6186 8.43551 12.5683 8.31032 12.4915L7.26445 14.1962C7.80406 14.5273 8.47961 14.6216 9.25644 14.751L9.58524 12.7782ZM5.83325 10.7107C5.83325 11.4979 5.81518 12.1795 6.05301 12.7662L7.90651 12.0148C7.85132 11.8787 7.83325 11.6814 7.83325 10.7107H5.83325ZM8.31032 12.4915C8.12826 12.3798 7.98675 12.2128 7.90651 12.0148L6.05301 12.7662C6.29374 13.36 6.71828 13.8612 7.26445 14.1962L8.31032 12.4915ZM13.8333 9.18087C13.8333 10.0882 13.8325 10.7099 13.7946 11.1876C13.7573 11.6573 13.6894 11.8898 13.6104 12.0428L15.3875 12.9603C15.6428 12.4658 15.742 11.929 15.7883 11.3458C15.834 10.7705 15.8333 10.0557 15.8333 9.18087H13.8333ZM9.25644 14.751C10.1198 14.8949 10.8251 15.0132 11.4002 15.0628C11.9832 15.113 12.5291 15.1035 13.0589 14.9329L12.4461 13.0291C12.2822 13.0819 12.0416 13.1107 11.5719 13.0702C11.0943 13.029 10.4806 12.9275 9.58524 12.7782L9.25644 14.751ZM13.6104 12.0428C13.3679 12.5125 12.9493 12.8671 12.4461 13.0291L13.0589 14.9329C14.0653 14.609 14.9025 13.8997 15.3875 12.9603L13.6104 12.0428ZM9.58645 3.22163C10.4814 3.07248 11.0948 2.97099 11.5722 2.92986C12.0416 2.88942 12.2822 2.91819 12.4461 2.97096L13.0589 1.06717C12.5292 0.896646 11.9834 0.887021 11.4005 0.937243C10.8256 0.986778 10.1206 1.10503 9.25766 1.24885L9.58645 3.22163ZM15.8333 6.81921C15.8333 5.9444 15.834 5.22954 15.7883 4.65428C15.742 4.07104 15.6428 3.53429 15.3875 3.0398L13.6104 3.95728C13.6894 4.1103 13.7573 4.34283 13.7946 4.81252C13.8325 5.29019 13.8333 5.91191 13.8333 6.81921H15.8333ZM12.4461 2.97096C12.9493 3.13294 13.3679 3.48755 13.6104 3.95728L15.3875 3.0398C14.9025 2.10035 14.0653 1.39113 13.0589 1.06717L12.4461 2.97096ZM9.25766 1.24885C8.83685 1.31898 8.47124 1.37935 8.17099 1.45102C7.85987 1.52528 7.55473 1.62545 7.26252 1.80503L8.30967 3.50898C8.35038 3.48397 8.42887 3.44565 8.63532 3.39637C8.85265 3.3445 9.13857 3.29628 9.58645 3.22163L9.25766 1.24885ZM7.83325 5.29122C7.83325 4.83715 7.83381 4.5472 7.84925 4.3243C7.86391 4.11256 7.88881 4.02884 7.90679 3.98457L6.05387 3.23179C5.92477 3.54955 5.87613 3.86701 5.85403 4.1861C5.8327 4.49405 5.83325 4.86461 5.83325 5.29122H7.83325ZM7.26252 1.80503C6.71792 2.13971 6.29446 2.63958 6.05387 3.23179L7.90679 3.98457C7.98699 3.78717 8.12814 3.62054 8.30967 3.50898L7.26252 1.80503Z"
									fill="currentColor"
								/>
							</svg>
							Thoát
						</button>
					</div>
					<div class="take__student">
						<img
							v-if="
								student.profile_photo?.w200 ||
								student.profile_photo?.url
							"
							:src="
								student.profile_photo.w200 ||
								student.profile_photo.url
							"
							alt=""
							class="take__avatar"
						/>
						<span
							v-else
							class="take__avatar take__avatar--fallback"
							:style="{
								background:
									student.profile_photo?.default?.color ||
									'#F1A267',
							}"
						>
							{{ student.profile_photo?.default?.initial || '?' }}
						</span>
						<div>
							<div class="take__name">
								{{ student.name || 'Học sinh' }}
							</div>
							<div v-if="ageLine" class="take__age">
								{{ ageLine }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</header>

		<main
			class="take__main"
			:class="{
				'take__main--done': view === 'completed',
				'take__main--question': view === 'question',
				'take__main--result': view === 'result',
			}"
		>
			<div v-if="loading" class="flex justify-center py-24">
				<Loading />
			</div>
			<ScreeningCompleted
				v-else-if="view === 'completed' && detail"
				:detail="detail"
				@send="sendOpen = true"
				@edit="startEdit"
				@delete="deleteOpen = true"
			/>
			<template v-else>
				<div class="take__intro">
					<span v-if="scaleName" class="take__badge mb-4">{{
						scaleName
					}}</span>
					<h1>{{ record?.title || 'Thang sàng lọc' }}</h1>
				</div>

				<section
					v-if="view === 'question' && question"
					class="take__card"
				>
					<div class="take__progress">
						<span>Mục {{ page }}</span>
						<div class="take__bar">
							<div :style="{ width: `${progress}%` }"></div>
						</div>
						<span>{{ answeredCount }}/{{ total }}</span>
					</div>
					<div class="take__question">
						<div class="take__index">
							{{ question.position || page }}
						</div>
						<div>
							<div class="take__q-title">
								{{ question.title }}
							</div>
							<p v-if="question.description" class="take__q-desc">
								{{ question.description }}
							</p>
						</div>
					</div>
					<button
						v-for="choice in choices"
						:key="choice.id"
						type="button"
						class="take__choice"
						:class="{
							'is-selected': selectedChoiceId === choice.id,
						}"
						@click="selectedChoiceId = choice.id"
					>
						<span class="take__radio"></span>
						<span class="take__score">{{
							formatScore(choice.score)
						}}</span>
						<span class="take__choice-copy">
							<span
								v-if="choice.title"
								class="take__choice-title"
								>{{ choice.title }}</span
							>
							<span
								v-if="choice.content"
								class="take__choice-content"
								>{{ choice.content }}</span
							>
						</span>
					</button>
				</section>

				<section
					v-else-if="view === 'result'"
					class="take__card take__card--result"
				>
					<div class="take__result-stack">
						<div class="take__result-head">
							<span class="take__result-badge">
								<img
									:src="clipboardIcon"
									alt=""
									width="20"
									height="20"
								/>
							</span>
							<h2>Kết quả đánh giá</h2>
						</div>
						<div class="take__result">
							<div class="take__ring">
								<svg viewBox="0 0 120 120">
									<circle
										class="is-track"
										cx="60"
										cy="60"
										r="46"
									/>
									<circle
										cx="60"
										cy="60"
										r="46"
										class="is-value"
										:style="{ stroke: ringColor }"
										:stroke-dasharray="ringLength"
										:stroke-dashoffset="ringOffset"
									/>
								</svg>
								<strong>{{ resultPercentLabel }}</strong>
							</div>
							<div class="take__stats">
								<div class="take__stat-stack">
									<div class="take__stat">
										<span>Tổng điểm</span>
										<b
											>{{
												result?.total_score ?? '—'
											}}
											điểm</b
										>
									</div>
									<div class="take__stat">
										<span>Kết luận</span>
										<b>{{ conclusionLabel }}</b>
									</div>
								</div>
								<div class="take__stat">
									<span>Ngày thực hiện</span>
									<b>{{
										formatDateTime(
											result?.date || record?.date
										)
									}}</b>
								</div>
							</div>
						</div>
						<ScreeningInsights :source="result" />
						<div v-if="scoreQuestions.length" class="take__scores">
							<div class="take__result-head justify-between">
								<div class="flex gap-4">
									<span class="take__result-badge">
										<img
											:src="listIcon"
											alt=""
											width="20"
											height="20"
										/>
									</span>
									<h2>Bảng điểm</h2>
								</div>
								<button
									type="button"
									class="take__scores-toggle"
									@click="toggleScores"
								>
									{{
										scoresOpen
											? 'Đóng bảng điểm'
											: 'Mở bảng điểm'
									}}
									<img
										:src="
											scoresOpen
												? chevronUpIcon
												: chevronDownIcon
										"
										alt=""
										width="16"
										height="16"
									/>
								</button>
							</div>
							<div v-if="scoresOpen" class="take__score-list">
								<div
									v-for="(item, index) in scoreQuestions"
									:key="questionIdOf(item, index)"
									class="take__score-item"
								>
									<button
										type="button"
										class="take__score-row"
										@click="
											toggleScoreQuestion(
												questionIdOf(item, index)
											)
										"
									>
										<span
											>{{ item.position || index + 1 }}.
											{{ item.title }}</span
										>
										<b>{{ questionScoreLabel(item) }}</b>
										<img
											:src="chevronDownIcon"
											alt=""
											width="20"
											height="20"
											:class="{
												'is-open':
													openedScores[
														questionIdOf(
															item,
															index
														)
													],
											}"
										/>
									</button>
									<div
										v-if="
											openedScores[
												questionIdOf(item, index)
											] && item.choices.length
										"
										class="take__score-choices"
									>
										<div
											v-for="choice in item.choices"
											:key="choice.id"
											class="take__score-choice"
											:class="{
												'is-selected': choiceSelected(
													item,
													choice
												),
											}"
										>
											<span
												class="take__score-choice-score"
												>{{
													choice.score
												}}</span
											>
											<span>
												<span
													v-if="choice.title"
													class="take__score-choice-title"
													>{{ choice.title }}</span
												>
												<span
													v-if="choice.content"
													class="take__score-choice-content"
													>{{ choice.content }}</span
												>
											</span>
										</div>
									</div>
								</div>
								<div class="take__score-total">
									<span>Tổng điểm</span>
									<b>{{ result?.total_score ?? '—' }} điểm</b>
								</div>
							</div>
						</div>
					</div>
					<div class="take__note">
						<svg
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
							aria-hidden="true"
						>
							<circle cx="12" cy="12" r="10" fill="#E68D7B" />
							<path
								d="M12 7.2v6"
								stroke="#fff"
								stroke-width="1.8"
								stroke-linecap="round"
							/>
							<circle cx="12" cy="16.6" r="1.1" fill="#fff" />
						</svg>
						<div>
							<strong>Lưu ý</strong>
							<p>
								Các công cụ trên được cung cấp nhằm mục đích
								tham khảo, sàng lọc hoặc hỗ trợ đánh giá. Kết
								quả không thay thế chẩn đoán hoặc đánh giá trực
								tiếp bởi bác sĩ/nhà chuyên môn.
							</p>
						</div>
					</div>
				</section>

				<div class="take__refs">
					<section
						class="take__ref take__ref--source"
						:class="{ 'is-closed': !sourceOpen }"
					>
						<span class="take__ref-icon">
							<img :src="bookCheckIcon" alt="" />
						</span>
						<div class="take__ref-main">
							<div class="take__ref-head">
								<button
									type="button"
									class="take__ref-title"
									@click="sourceOpen = !sourceOpen"
								>
									Nguồn tham khảo
								</button>
								<button
									v-if="sourceOpen"
									type="button"
									class="take__ref-close"
									aria-label="Đóng nguồn tham khảo"
									@click="sourceOpen = false"
								>
									<img :src="closeSourceIcon" alt="" />
								</button>
								<button
									v-else
									type="button"
									class="take__ref-close"
									aria-label="Mở nguồn tham khảo"
									@click="sourceOpen = true"
								>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
									>
										<path
											d="M5 7.5L10 12.5L15 7.5"
											stroke="#94A3B8"
											stroke-width="1.6"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</button>
							</div>
							<p v-if="sourceOpen" class="take__ref-text">
								{{ scaleSource || 'Chưa có nguồn tham khảo.' }}
							</p>
						</div>
					</section>

					<section
						class="take__ref take__ref--guide"
						:class="{ 'is-closed': !guideOpen }"
					>
						<span class="take__ref-icon">
							<img :src="bulbIcon" alt="" />
						</span>
						<div class="take__ref-main">
							<div class="take__ref-head">
								<button
									type="button"
									class="take__ref-title"
									@click="guideOpen = !guideOpen"
								>
									Hướng dẫn thực hiện, chấm điểm và đánh giá
								</button>
								<button
									v-if="guideOpen"
									type="button"
									class="take__ref-close"
									aria-label="Đóng hướng dẫn"
									@click="guideOpen = false"
								>
									<img :src="closeGuideIcon" alt="" />
								</button>
								<button
									v-else
									type="button"
									class="take__ref-close"
									aria-label="Mở hướng dẫn"
									@click="guideOpen = true"
								>
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
									>
										<path
											d="M5 7.5L10 12.5L15 7.5"
											stroke="#94A3B8"
											stroke-width="1.6"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</button>
							</div>
							<ul v-if="guideOpen" class="take__ref-list">
								<li>
									Thang đánh giá gồm 15 mục, mỗi mục có 4 mức
									độ.
								</li>
								<li>
									Người đánh giá quan sát trẻ, đánh giá các
									hành vi tương ứng với mỗi mức độ của mục đó.
									Với mỗi mục, khoanh trong vào số tương ứng
									với tình trạng mô tả đúng nhất của trẻ.
								</li>
								<li>
									Bạn có thể chỉ ra trẻ với tình trạng nằm
									giữa hai mức độ bằng việc cho điểm 1.5; 2.5
									hoặc 3.5.
								</li>
							</ul>
						</div>
					</section>
				</div>

			</template>
		</main>

		<div
			v-if="view === 'result' && !loading"
			class="take__actions--sticky"
		>
			<div class="take__actions">
				<button
					type="button"
					class="take__prev"
					@click="backToQuestion"
				>
					← Quay lại
				</button>
				<button
					type="button"
					class="button-p take__next"
					:disabled="loadingSimple"
					@click="openSimple"
				>
					{{
						loadingSimple
							? 'Đang tải...'
							: 'Tạo kết quả sàng lọc'
					}}
					<span v-if="!loadingSimple">→</span>
				</button>
			</div>
		</div>

		<div
			v-if="view === 'question' && question && !loading"
			class="take__actions--sticky"
		>
			<div class="take__actions">
				<button
					type="button"
					class="take__prev"
					:disabled="page <= 1 || saving"
					@click="prev"
				>
					← Câu trước
				</button>
				<button
					type="button"
					class="button-p take__next"
					:disabled="!selectedChoiceId || saving"
					@click="next"
				>
					{{
						saving
							? 'Đang lưu...'
							: isLast
							? 'Xem kết quả'
							: 'Câu tiếp theo'
					}}
					<span v-if="!saving">→</span>
				</button>
			</div>
		</div>

		<ScreeningExitModal
			v-if="exitOpen"
			@close="exitOpen = false"
			@confirm="leave"
		/>
		<ScreeningSendModal
			v-if="sendOpen"
			:loading="sendingParent"
			@close="sendOpen = false"
			@confirm="confirmSendParent"
		/>
		<ScreeningResultForm
			v-if="formOpen && formInfo"
			:info="formInfo"
			:student-id="studentId"
			:screening-id="screeningId"
			:saving="submittingResult"
			:saved="resultSaved"
			:editing="editing"
			@close="closeResult"
			@submit="submitResult"
		/>
		<ScreeningDeleteModal
			v-if="deleteOpen"
			:loading="deleting"
			@close="deleteOpen = false"
			@confirm="confirmDelete"
		/>
	</div>
</template>

<style lang="scss" scoped>
.take {
	min-height: 100vh;
	background: #f8fafc;
	color: #344256;
}

.take__header {
	width: 100%;
	max-width: 1104px;
	margin-left: auto;
	margin-right: auto;
	padding: 28px 48px 12px;
}

.take__header-inner {
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 24px;
}

.take__crumbs {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 20px;
	color: #65758b;
	font-size: 14px;
	font-weight: 600;
}

.take__student {
	display: flex;
	align-items: center;
	gap: 20px;
}

.take__avatar {
	width: 64px;
	height: 64px;
	border-radius: 999px;
	object-fit: cover;
	border: 2px solid #f1a267;
}

.take__avatar--fallback {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #fff;
	font-weight: 600;
}

.take__name {
	font-size: 28px;
	line-height: 32px;
	font-weight: 700;
	letter-spacing: -0.56px;
}

.take__age {
	margin-top: 8px;
	color: #2aa797;
	font-size: 12px;
	line-height: 16px;
	font-weight: 600;
	letter-spacing: 0.96px;
	text-transform: uppercase;
}

.take__exit {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	color: #65758b;
	font-size: 14px;
	font-weight: 600;
}

.take__main {
	width: 100%;
	max-width: 1104px;
	margin-left: auto;
	margin-right: auto;
	padding: 12px 48px 64px;
}

.take--done {
	.take__header,
	.take__main {
		max-width: 1200px;
	}
}

.take__main--done {
	padding-top: 36px;
}

.take__intro {
	margin-bottom: 40px;

	h1 {
		margin: 0;
		font-size: 33px;
		line-height: 40px;
		font-weight: 600;
		letter-spacing: -0.66px;
	}
}

.take__badge {
	display: inline-flex;
	padding: 8px 16px;
	border-radius: 100px;
	background: #e0e7ff;
	color: #4f46e5;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
}

.take__refs {
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin-top: 40px;
}

.take__ref {
	display: flex;
	align-items: flex-start;
	gap: 20px;
	width: 100%;
	padding: 24px;
	border-radius: 8px;
	border: 1px solid transparent;
	color: #344256;
}

.take__ref--source {
	background: #f0f9ff;
	border-color: #bae6fd;
}

.take__ref--guide {
	background: #fef8ed;
	border-color: #f6d692;
}

.take__ref.is-closed {
	align-items: center;
}

.take__ref-icon {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
}

.take__ref-main {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
}

.take__ref-head {
	display: flex;
	align-items: flex-end;
	gap: 4px;
	width: 100%;
}

.take__ref-title {
	flex: 1;
	padding: 0;
	border: 0;
	background: transparent;
	color: #344256;
	font-family: inherit;
	font-size: 18px;
	font-weight: 600;
	line-height: 24px;
	text-align: left;
}

.take__ref-close {
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	padding: 0;
	border: 0;
	background: transparent;
}

.take__ref-text {
	margin: 0;
	color: #344256;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	white-space: pre-line;
}

.take__ref-list {
	margin: 0;
	padding: 0;
	color: #344256;
	font-size: 16px;
	font-weight: 400;
	line-height: 24px;
	list-style: disc;

	li {
		margin-left: 24px;
	}
}

.take__card {
	margin-top: 0;
	padding: 28px;
	background: #fff;
	border-radius: 16px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.1), 0 8px 16px rgba(20, 37, 63, 0.04);
}

.take__progress {
	display: flex;
	align-items: center;
	gap: 12px;
	margin-bottom: 24px;
	color: #65758b;
	font-size: 14px;
	font-weight: 600;
}

.take__bar {
	flex: 1;
	height: 8px;
	border-radius: 99px;
	background: #e8eef5;
	overflow: hidden;

	div {
		height: 100%;
		background: #f0bb4f;
		border-radius: 99px;
	}
}

.take__question {
	display: flex;
	gap: 16px;
	margin-bottom: 20px;
	align-items: center;
}

.take__index {
	width: 36px;
	height: 36px;
	flex-shrink: 0;
	border-radius: 999px;
	background: #4f46e5;
	color: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 700;
}

.take__q-title {
	font-size: 18px;
	line-height: 28px;
	font-weight: 700;
}

.take__q-desc {
	margin-top: 4px;
	color: #65758b;
	font-size: 16px;
	line-height: 24px;
}

.take__choice {
	display: flex;
	align-items: flex-start;
	gap: 12px;
	width: 100%;
	margin-top: 12px;
	padding: 16px;
	text-align: left;
	border: 1px solid #e1e7ef;
	border-radius: 12px;
	background: #fff;

	&.is-selected {
		border-color: #4f46e5;
		background: #f8faff;
	}
}

.take__radio {
	width: 20px;
	height: 20px;
	margin-top: 2px;
	flex-shrink: 0;
	border-radius: 999px;
	border: 2px solid #cbd5e1;
	background: #fff;
}

.take__choice.is-selected .take__radio {
	border-color: #4f46e5;
	background: radial-gradient(circle, #fff 0 3px, #4f46e5 4px);
}

.take__score {
	width: 36px;
	flex-shrink: 0;
	font-weight: 700;
}

.take__choice-copy {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

.take__choice-title {
	font-weight: 600;
	line-height: 24px;
}

.take__choice-content {
	color: #65758b;
	line-height: 24px;
}

.take__main--question,
.take__main--result {
	padding-bottom: 120px;
}

.take__actions {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	margin-top: 28px;
}

.take__actions--sticky {
	position: fixed;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 20;
	width: 100%;
	padding: 16px 0 24px;
	background: #fff;
	box-sizing: border-box;
	box-shadow: 0 -1px 0 #e8eef5;

	.take__actions {
		width: 100%;
		max-width: 1104px;
		margin: 0 auto;
		padding: 0 48px;
		box-sizing: border-box;
	}
}

.take__prev {
	color: #94a3b8;
	font-size: 14px;
	font-weight: 600;

	&:disabled {
		opacity: 0.45;
		cursor: default;
	}
}

.take__next {
	width: auto;
	min-width: 180px;
	padding: 0 20px;
	gap: 8px;
}

.take__card--result {
	display: flex;
	flex-direction: column;
	gap: 24px;
	padding: 24px;
	border-radius: 8px;
	box-shadow: 0 0 1px rgba(12, 26, 75, 0.24), 0 3px 8px rgba(50, 50, 71, 0.05);

	.take__note {
		margin-top: 0;
	}
}

.take__result-stack {
	display: flex;
	flex-direction: column;
	gap: 40px;
}

.take__result-head {
	display: flex;
	align-items: center;
	gap: 16px;

	h2 {
		margin: 0;
		color: #344256;
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}
}

.take__result-badge {
	display: inline-flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 100px;
	background: #6366f1;

	img {
		width: 20px;
		height: 20px;
	}
}

.take__result {
	display: flex;
	align-items: center;
	gap: 60px;
}

.take__ring {
	position: relative;
	width: 167px;
	height: 167px;
	flex-shrink: 0;

	svg {
		width: 167px;
		height: 167px;
		transform: rotate(-90deg);
	}

	circle {
		fill: none;
		stroke-width: 8;
	}

	.is-track {
		stroke: #e8eef5;
	}

	.is-value {
		stroke-linecap: round;
	}

	strong {
		position: absolute;
		inset: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 32px;
		font-weight: 600;
		line-height: 40px;
		letter-spacing: -0.64px;
	}
}

.take__stats {
	display: flex;
	flex: 1;
	align-items: flex-start;
	gap: 16px;
	min-width: 0;
	color: #344256;
}

.take__stat-stack {
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
}

.take__stats > .take__stat {
	flex: 1;
}

.take__stat {
	display: flex;
	flex-direction: column;
	gap: 4px;

	span {
		font-size: 18px;
		line-height: 24px;
		font-weight: 400;
	}

	b {
		font-size: 20px;
		line-height: 28px;
		font-weight: 600;
	}
}

.take__scores {
	display: flex;
	flex-direction: column;
	gap: 24px;
}

.take__scores-toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 0;
	border: 0;
	background: transparent;
	color: #0ea5e9;
	font-family: inherit;
	font-size: 14px;
	line-height: 20px;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;

	img {
		width: 16px;
		height: 16px;
	}
}

.take__score-list {
	display: flex;
	flex-direction: column;
	gap: 20px;
	padding-left: 60px;
}

.take__score-item {
	display: flex;
	flex-direction: column;
	gap: 12px;
	padding-bottom: 16px;
	border-bottom: 1px solid #e1e7ef;
}

.take__score-row,
.take__score-total {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	width: 100%;
	padding: 0;
	border: 0;
	background: transparent;
	color: #344256;
	font-family: inherit;
	font-size: 18px;
	line-height: 24px;
	font-weight: 600;
	text-align: left;
	cursor: pointer;

	span {
		flex: 1;
		min-width: 0;
	}

	b {
		flex-shrink: 0;
		color: #2aa797;
		font-weight: 600;
	}

	img {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
		transition: transform 0.15s ease;

		&.is-open {
			transform: rotate(180deg);
		}
	}
}

.take__score-total {
	cursor: default;
}

.take__score-choices {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.take__score-choice {
	display: flex;
	align-items: flex-start;
	gap: 24px;
	padding: 20px 24px;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	background: #fff;
	color: #344256;
	font-size: 16px;
	line-height: 24px;

	&.is-selected {
		background: #eef2ff;
		border-color: #a5b4fc;
	}
}

.take__score-choice-score {
	width: 36px;
	flex-shrink: 0;
	font-weight: 600;
}

.take__score-choice-title {
	display: block;
	font-weight: 600;
}

.take__score-choice-content {
	display: block;
	font-weight: 400;
}

.take__note {
	display: flex;
	align-items: flex-start;
	gap: 20px;
	margin-top: 24px;
	padding: 24px;
	border-radius: 8px;
	background: #fcf1ee;
	border: 1px solid #eba99c;

	svg {
		flex-shrink: 0;
	}

	strong {
		display: block;
		color: #344256;
		font-size: 16px;
		line-height: 24px;
		font-weight: 600;
	}

	p {
		margin-top: 8px;
		color: #344256;
		font-size: 14px;
		line-height: 20px;
	}
}

@media (max-width: 800px) {
	.take__header,
	.take__main {
		padding-left: 16px;
		padding-right: 16px;
	}

	.take__result {
		flex-direction: column;
		align-items: flex-start;
		gap: 24px;
	}

	.take__stats,
	.take__stat-stack {
		flex-direction: column;
		gap: 16px;
	}
}
</style>
